import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { CONTACT } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [params] = useSearchParams();
  const presetProduct = params.get("product") || "";
  const [form, setForm] = useState({ name: "", phone: "", email: "", product: presetProduct, requirement: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.requirement.trim()) {
      toast.error("Please fill in your name, phone and requirement.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      toast.success("Enquiry sent. Our team will get back to you shortly.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-[#F8F9FA] py-20 md:py-28 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Contact Us</Eyebrow>
            <h1 className="mt-4 max-w-3xl text-5xl md:text-6xl font-bold tracking-tighter" data-testid="contact-heading">
              Tell us what you need in stone
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-gray-600">
              Share your requirement below, call us, or message us on WhatsApp — whichever is easiest for you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            {submitted ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center border border-gray-200 bg-[#F8F9FA] p-10 text-center" data-testid="enquiry-success">
                <CheckCircle2 size={48} className="text-[#D40000]" />
                <h2 className="mt-6 text-3xl font-semibold tracking-tight">Enquiry received</h2>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-gray-600">
                  Thank you, {form.name.split(" ")[0]}. Our team will review your requirement and reach out on{" "}
                  {form.phone} shortly.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", product: "", requirement: "" }); }}
                  data-testid="enquiry-another-button"
                  className="mt-8 border border-black px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-1 hover:bg-black hover:text-white"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-gray-200 bg-[#F8F9FA] p-8 md:p-10" data-testid="enquiry-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your full name"
                      data-testid="enquiry-name-input"
                      className="rounded-none border-gray-300 bg-white focus-visible:ring-[#D40000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 ..."
                      data-testid="enquiry-phone-input"
                      className="rounded-none border-gray-300 bg-white focus-visible:ring-[#D40000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@company.com"
                      data-testid="enquiry-email-input"
                      className="rounded-none border-gray-300 bg-white focus-visible:ring-[#D40000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Product Interest</Label>
                    <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                      <SelectTrigger data-testid="enquiry-product-select" className="rounded-none border-gray-300 bg-white focus:ring-[#D40000]">
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-white">
                        <SelectItem value="Granite" data-testid="enquiry-product-granite">Granite</SelectItem>
                        <SelectItem value="Marble" data-testid="enquiry-product-marble">Marble</SelectItem>
                        <SelectItem value="Dolomite" data-testid="enquiry-product-dolomite">Dolomite</SelectItem>
                        <SelectItem value="Multiple / Not sure" data-testid="enquiry-product-multiple">Multiple / Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="requirement">Your Requirement *</Label>
                    <Textarea
                      id="requirement"
                      value={form.requirement}
                      onChange={set("requirement")}
                      rows={5}
                      placeholder="Stone type, form (blocks / slabs / tiles / powder), quantity and delivery location..."
                      data-testid="enquiry-requirement-input"
                      className="rounded-none border-gray-300 bg-white focus-visible:ring-[#D40000]"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-button"
                  className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#D40000] px-10 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {submitting ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            )}
          </Reveal>

          {/* Info */}
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="space-y-6">
              <div className="border border-gray-200 p-7" data-testid="contact-call-card">
                <Phone size={24} className="text-[#D40000]" strokeWidth={1.5} />
                <h3 className="mt-4 text-xl font-medium">Call Us</h3>
                <p className="mt-1 text-sm text-gray-600">Speak directly with our sales team.</p>
                <a href={CONTACT.phoneHref} data-testid="contact-call-link" className="mt-3 block text-lg font-bold text-black hover:text-[#D40000]">
                  {CONTACT.phoneDisplay}
                </a>
                <a href={CONTACT.phone2Href} data-testid="contact-call-link-2" className="mt-1 block text-lg font-bold text-black hover:text-[#D40000]">
                  {CONTACT.phone2Display}
                </a>
                <a href={CONTACT.phoneUsHref} data-testid="contact-call-link-us" className="mt-1 block text-sm font-semibold text-gray-600 hover:text-[#D40000]">
                  International: {CONTACT.phoneUsDisplay}
                </a>
              </div>
              <div className="border border-gray-200 p-7" data-testid="contact-whatsapp-card">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-[#25D366]" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <h3 className="mt-4 text-xl font-medium">WhatsApp</h3>
                <p className="mt-1 text-sm text-gray-600">Send photos or drawings of your requirement.</p>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="mt-4 inline-flex items-center gap-2 border border-black px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-1 hover:bg-black hover:text-white"
                >
                  Chat Now
                </a>
              </div>
              <div className="border border-gray-200 p-7" data-testid="contact-visit-card">
                <MapPin size={24} className="text-[#D40000]" strokeWidth={1.5} />
                <h3 className="mt-4 text-xl font-medium">Visit Our Facility</h3>
                <p className="mt-1 text-sm text-gray-600" data-testid="contact-address">{CONTACT.address}</p>
                <p className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <Mail size={14} /> <span data-testid="contact-email">{CONTACT.email}</span>
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm text-gray-600" data-testid="contact-website">
                  {CONTACT.website}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
